import React, { useState, useEffect } from 'react'
import {
  Table,
  Header,
  SpaceBetween,
  Button,
  Badge,
  Box,
  Modal,
  Form,
  FormField,
  Input,
  Select,
  Container,
  StatusIndicator,
  Alert,
  Multiselect,
  Icon
} from '@cloudscape-design/components'
import { apiService } from '../services/api'
import { useRole } from '../contexts/RoleContext'

interface Device {
  id: string
  deviceName: string
  userId: string
  username?: string
  userEmail?: string
  status: 'online' | 'offline' | 'error'
  lastApiCall: string
  registeredAt: string
  authorizedDataProducts: string[]
  ipAddress?: string
  firmwareVersion?: string
  assignedLocationList?: {
    dataProduct: string
    locationCount: number
  }
  configuration?: {
    ledCount: number
    ledPin: number
    brightness: number
    nightModeBrightness: number
    pollingInterval: number
    legendEnabled?: boolean
    rotationIntervalSeconds?: number
    sleepMode?: {
      enabled: boolean
      startHour: number
      startMinute: number
      endHour: number
      endMinute: number
      timezone: string
    }
  }
}

const Devices: React.FC = () => {
  const { isAdmin, isProduction, isLoading: roleLoading } = useRole()
  const [devices, setDevices] = useState<Device[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedItems, setSelectedItems] = useState<Device[]>([])
  const [showRegisterModal, setShowRegisterModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAuthTokenModal, setShowAuthTokenModal] = useState(false)
  const [authTokenData, setAuthTokenData] = useState<{ deviceId: string; deviceName: string; authToken: string } | null>(null)
  const [editingDevice, setEditingDevice] = useState<Device | null>(null)
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null)
  const [newDevice, setNewDevice] = useState({
    deviceName: '',
    userId: '',
    dataProducts: [] as string[]
  })

  const [userOptions, setUserOptions] = useState<Array<{ label: string; value: string }>>([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [userLocationLists, setUserLocationLists] = useState<Record<string, any[]>>({})
  const [loadingLocationLists, setLoadingLocationLists] = useState(false)
  
  // TODO: Add role-based filtering
  // - Admin/Production users see all devices with owner column
  // - Standard Users see only their own devices
  // - Add owner filter for Admin/Production users
  
  // Timezone options for sleep mode
  const timezoneOptions = [
    { label: 'Eastern Time (US)', value: 'America/New_York' },
    { label: 'Central Time (US)', value: 'America/Chicago' },
    { label: 'Mountain Time (US)', value: 'America/Denver' },
    { label: 'Pacific Time (US)', value: 'America/Los_Angeles' },
    { label: 'Alaska Time', value: 'America/Anchorage' },
    { label: 'Hawaii Time', value: 'Pacific/Honolulu' },
    { label: 'UTC', value: 'UTC' },
  ]

  const dataProductOptions = [
    { label: 'METAR Weather', value: 'metar' },
    { label: 'Marine Conditions', value: 'marine' },
    { label: 'Trail Conditions', value: 'trail' },
    { label: 'NWS Observations', value: 'nws' },
    { label: 'HF Radio Propagation', value: 'hf_propagation' },
    { label: 'Beach Conditions', value: 'beach_conditions' }
  ]

  const canManageDevices = isAdmin() || isProduction()

  useEffect(() => {
    if (roleLoading) return
    loadDevices()
    if (canManageDevices) {
      loadUsers()
    }
  }, [roleLoading])

  const loadUserLocationLists = async (userId: string) => {
    if (!userId) return
    
    setLoadingLocationLists(true)
    try {
      const response = await apiService.getUserLocationLists(userId)
      
      if (response.statusCode === 200 && response.data) {
        // Store location lists for this user
        setUserLocationLists(prev => ({
          ...prev,
          [userId]: response.data.location_lists || []
        }))
      }
    } catch (error) {
      console.error('Error loading location lists:', error)
    } finally {
      setLoadingLocationLists(false)
    }
  }

  const loadUsers = async () => {
    setLoadingUsers(true)
    try {
      const response = await apiService.getUsers()
      
      if (response.statusCode === 200 && response.data) {
        let usersData = response.data
        
        // Handle different response formats
        if (response.data && typeof response.data === 'object' && 'users' in response.data) {
          usersData = (response.data as any).users
        } else if (!Array.isArray(response.data)) {
          usersData = [response.data]
        }
        
        // Transform users to options format - use real names, never Cognito sub hashes
        const options = usersData.map((user: any) => {
          const givenName = user.given_name || user.givenName || '';
          const familyName = user.family_name || user.familyName || '';
          const fullName = [givenName, familyName].filter(Boolean).join(' ');
          const displayName = fullName || user.email?.split('@')[0] || 'Unknown';
          return {
            label: `${displayName} (${user.email})`,
            value: user.user_id || user.userId || user.id
          };
        })
        
        setUserOptions(options)
      } else {
        console.error('Failed to load users for dropdown')
        setUserOptions([])
      }
    } catch (error) {
      console.error('Error loading users for dropdown:', error)
      setUserOptions([])
    } finally {
      setLoadingUsers(false)
    }
  }

  const loadDevices = async () => {
    setLoading(true)
    try {
      const response = await apiService.getDevices()
      
      if (response.statusCode === 200 && response.data) {
        // Handle different response formats from the API
        let devicesData = response.data
        
        // If the response has a nested structure, extract the devices array
        if (response.data && typeof response.data === 'object' && 'devices' in response.data) {
          devicesData = (response.data as any).devices
        } else if (Array.isArray(response.data)) {
          devicesData = response.data
        }
        
        // Transform API data to match our interface
        const transformedDevices = devicesData.map((device: any) => {
          const config = device.configuration || {}
          const assignedList = config.assigned_location_list
          
          return {
            id: device.device_id || device.deviceId || device.id,
            deviceName: device.device_name || device.deviceName || 'Unknown Device',
            userId: device.user_id || device.userId,
            username: device.user_name || device.userName || device.username || 'Unknown User',
            userEmail: device.user_email || device.userEmail || '',
            status: determineDeviceStatus(device.last_api_call || device.lastApiCall),
            lastApiCall: formatLastApiCall(device.last_api_call || device.lastApiCall),
            registeredAt: formatDate(device.registered_at || device.registeredAt),
            authorizedDataProducts: device.authorized_data_products || device.authorizedDataProducts || [],
            ipAddress: device.ip_address || device.ipAddress,
            firmwareVersion: device.firmware_version || device.firmwareVersion,
            configuration: config,
            assignedLocationList: assignedList ? {
              dataProduct: assignedList.data_product,
              locationCount: assignedList.location_count || 0
            } : undefined
          }
        })
        setDevices(transformedDevices)
      } else if (response.statusCode === 502) {
        throw new Error('Server error - Lambda function may be experiencing issues')
      } else {
        throw new Error(response.error || 'Failed to load devices')
      }
    } catch (error) {
      console.error('Error loading devices:', error)
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load devices. Please try again.'
      })
      setDevices([])
    } finally {
      setLoading(false)
    }
  }

  const determineDeviceStatus = (lastApiCall: string): 'online' | 'offline' | 'error' => {
    if (!lastApiCall || lastApiCall === 'Never') return 'offline'
    
    try {
      const lastCall = new Date(lastApiCall)
      const now = new Date()
      const diffMinutes = (now.getTime() - lastCall.getTime()) / (1000 * 60)
      
      if (diffMinutes < 30) return 'online'
      if (diffMinutes < 120) return 'offline'
      return 'error'
    } catch {
      return 'offline'
    }
  }

  const formatLastApiCall = (lastApiCall: string): string => {
    if (!lastApiCall) return 'Never'
    
    try {
      const date = new Date(lastApiCall)
      const now = new Date()
      const diffMinutes = (now.getTime() - date.getTime()) / (1000 * 60)
      
      if (diffMinutes < 1) return 'Just now'
      if (diffMinutes < 60) return `${Math.floor(diffMinutes)} minutes ago`
      if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)} hours ago`
      return `${Math.floor(diffMinutes / 1440)} days ago`
    } catch {
      return lastApiCall
    }
  }

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Unknown'
    
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return dateString
    }
  }

  const getStatusIndicator = (status: string) => {
    switch (status) {
      case 'online':
        return <StatusIndicator type="success">Online</StatusIndicator>
      case 'offline':
        return <StatusIndicator type="stopped">Offline</StatusIndicator>
      case 'error':
        return <StatusIndicator type="error">Error</StatusIndicator>
      default:
        return <StatusIndicator type="pending">Unknown</StatusIndicator>
    }
  }

  const handleEditDevice = (device: Device) => {
    // Initialize configuration with defaults if not present
    const existingConfig: any = device.configuration || {}
    const deviceWithConfig = {
      ...device,
      configuration: {
        ledCount: existingConfig.ledCount ?? 30,
        ledPin: existingConfig.ledPin ?? 2,
        brightness: existingConfig.brightness ?? 128,
        nightModeBrightness: existingConfig.nightModeBrightness ?? 32,
        pollingInterval: existingConfig.pollingInterval ?? 900,
        legendEnabled: existingConfig.legendEnabled ?? false,
        rotationIntervalSeconds: existingConfig.rotationIntervalSeconds ?? 5,
        sleepMode: {
          enabled: existingConfig.sleepMode?.enabled ?? false,
          startHour: existingConfig.sleepMode?.startHour ?? 22,
          startMinute: existingConfig.sleepMode?.startMinute ?? 0,
          endHour: existingConfig.sleepMode?.endHour ?? 7,
          endMinute: existingConfig.sleepMode?.endMinute ?? 0,
          timezone: existingConfig.sleepMode?.timezone ?? 'America/New_York'
        }
      }
    }
    setEditingDevice(deviceWithConfig)
    setShowEditModal(true)
    // Load location lists for the device owner
    if (device.userId) {
      loadUserLocationLists(device.userId)
    }
  }

  const handleSaveDevice = async () => {
    if (!editingDevice) return

    try {
      // Update device authorization via API
      const authResponse = await apiService.updateDeviceAuthorization(
        editingDevice.id,
        editingDevice.authorizedDataProducts
      )
      
      if (authResponse.statusCode !== 200) {
        throw new Error(authResponse.error || 'Failed to update device authorization')
      }
      
      // If location list was assigned, update that too
      if (editingDevice.assignedLocationList) {
        const listResponse = await apiService.assignLocationListToDevice(
          editingDevice.id,
          editingDevice.userId,
          editingDevice.assignedLocationList.dataProduct
        )
        
        if (listResponse.statusCode !== 200) {
          throw new Error(listResponse.error || 'Failed to assign location list')
        }
      }
      
      // Update device configuration
      if (editingDevice.configuration) {
        const configResponse = await apiService.updateDeviceConfiguration(
          editingDevice.id,
          editingDevice.configuration
        )
        
        if (configResponse.statusCode !== 200) {
          throw new Error(configResponse.error || 'Failed to update device configuration')
        }
      }
      
      // Reload devices from server to get fresh data
      await loadDevices()
      setShowEditModal(false)
      setEditingDevice(null)
      setAlert({
        type: 'success',
        message: 'Device updated successfully'
      })
    } catch (error) {
      console.error('Error updating device:', error)
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update device. Please try again.'
      })
    }
  }

  const handleRegisterDevice = async () => {
    try {
      const response = await apiService.registerDevice({
        operation: 'register',
        user_id: newDevice.userId,
        device_name: newDevice.deviceName,
        device_type: 'esp32'
      })
      
      if (response.statusCode === 200 || response.statusCode === 201) {
        // Extract auth token from response
        const responseData = response.data
        const authToken = responseData.auth_token || responseData.authToken
        const deviceId = responseData.device_id || responseData.deviceId
        const deviceName = responseData.device_name || responseData.deviceName || newDevice.deviceName
        
        // Show auth token modal
        setAuthTokenData({
          deviceId,
          deviceName,
          authToken
        })
        setShowAuthTokenModal(true)
        
        await loadDevices() // Reload devices list
        setShowRegisterModal(false)
        setNewDevice({ deviceName: '', userId: '', dataProducts: [] })
      } else {
        throw new Error(response.error || 'Failed to register device')
      }
    } catch (error) {
      console.error('Error registering device:', error)
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to register device. Please try again.'
      })
    }
  }

  const handleRevokeDevice = async (deviceId: string) => {
    try {
      const response = await apiService.revokeDevice(deviceId)
      
      if (response.statusCode === 200) {
        await loadDevices() // Reload devices list
        setAlert({
          type: 'success',
          message: 'Device access revoked successfully'
        })
      } else {
        throw new Error(response.error || 'Failed to revoke device')
      }
    } catch (error) {
      console.error('Error revoking device:', error)
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to revoke device. Please try again.'
      })
    }
  }

  const handleDeleteDevice = async (device: Device) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${device.deviceName}"? This cannot be undone.`)) {
      return
    }

    try {
      const response = await apiService.deleteDevice(device.id)
      
      if (response.statusCode === 200) {
        await loadDevices()
        setAlert({
          type: 'success',
          message: `Device "${device.deviceName}" deleted successfully`
        })
      } else {
        throw new Error(response.error || 'Failed to delete device')
      }
    } catch (error) {
      console.error('Error deleting device:', error)
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to delete device. Please try again.'
      })
    }
  }

  const handleCopyAuthToken = () => {
    if (authTokenData?.authToken) {
      navigator.clipboard.writeText(authTokenData.authToken)
      setAlert({
        type: 'success',
        message: 'Auth token copied to clipboard!'
      })
    }
  }

  const handleRegenerateKey = async (device: Device) => {
    if (!window.confirm(`Are you sure you want to regenerate the auth key for "${device.deviceName}"? The old key will stop working immediately.`)) {
      return
    }

    try {
      // Revoke the old device and register a new one with the same name
      await apiService.revokeDevice(device.id)
      
      const response = await apiService.registerDevice({
        operation: 'register',
        user_id: device.userId,
        device_name: device.deviceName,
        device_type: 'esp32'
      })
      
      if (response.statusCode === 200 || response.statusCode === 201) {
        // Extract auth token from response
        const responseData = response.data
        const authToken = responseData.auth_token || responseData.authToken
        const deviceId = responseData.device_id || responseData.deviceId
        const deviceName = responseData.device_name || responseData.deviceName || device.deviceName
        
        // Show auth token modal
        setAuthTokenData({
          deviceId,
          deviceName,
          authToken
        })
        setShowAuthTokenModal(true)
        
        await loadDevices() // Reload devices list
        setAlert({
          type: 'success',
          message: 'Device key regenerated successfully'
        })
      } else {
        throw new Error(response.error || 'Failed to regenerate device key')
      }
    } catch (error) {
      console.error('Error regenerating device key:', error)
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to regenerate device key. Please try again.'
      })
    }
  }

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            {canManageDevices && (
              <>
                <Button
                  disabled={selectedItems.length === 0}
                  onClick={() => {
                    selectedItems.forEach(device => handleRevokeDevice(device.id))
                    setSelectedItems([])
                  }}
                >
                  Revoke Selected
                </Button>
                <Button 
                  variant="primary" 
                  onClick={() => setShowRegisterModal(true)}
                >
                  Register Device
                </Button>
              </>
            )}
            <Button 
              onClick={loadDevices}
              loading={loading}
            >
              Refresh
            </Button>
          </SpaceBetween>
        }
      >
        {canManageDevices ? 'Device Management' : 'My Devices'}
      </Header>

      {alert && (
        <Alert
          type={alert.type}
          dismissible
          onDismiss={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Container>
        <Table
          columnDefinitions={[
            ...(canManageDevices ? [{
              id: 'username',
              header: 'Owner',
              cell: (item: Device) => item.userEmail ? `${item.username} (${item.userEmail})` : item.username || 'Unknown',
              sortingField: 'username'
            }] : []),
            {
              id: 'deviceName',
              header: 'Device Name',
              cell: item => item.deviceName,
              sortingField: 'deviceName'
            },
            {
              id: 'registeredAt',
              header: 'Registered',
              cell: item => item.registeredAt,
              sortingField: 'registeredAt'
            },
            {
              id: 'dataProducts',
              header: 'Products',
              cell: item => (
                <SpaceBetween direction="horizontal" size="xs">
                  {item.authorizedDataProducts.map(product => (
                    <Badge key={product} color="blue">
                      {dataProductOptions.find(opt => opt.value === product)?.label || product}
                    </Badge>
                  ))}
                  {item.authorizedDataProducts.length === 0 && (
                    <Badge color="grey">None</Badge>
                  )}
                </SpaceBetween>
              )
            },
            {
              id: 'locationList',
              header: 'Location List',
              cell: item => item.assignedLocationList ? (
                <Badge color="green">
                  {dataProductOptions.find(opt => opt.value === item.assignedLocationList?.dataProduct)?.label || item.assignedLocationList.dataProduct}
                  {' '}({item.assignedLocationList.locationCount})
                </Badge>
              ) : (
                <Badge color="grey">Not assigned</Badge>
              )
            },
            {
              id: 'status',
              header: 'Status',
              cell: item => getStatusIndicator(item.status)
            },
            {
              id: 'lastApiCall',
              header: 'Last API Call',
              cell: item => item.lastApiCall,
              sortingField: 'lastApiCall'
            },
            {
              id: 'actions',
              header: 'Actions',
              minWidth: 120,
              cell: item => (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="icon" iconName="edit" onClick={() => handleEditDevice(item)} ariaLabel="Edit device" />
                  {canManageDevices && (
                    <>
                      <Button variant="icon" iconName="key" onClick={() => handleRegenerateKey(item)} ariaLabel="Regenerate key" />
                      <Button variant="icon" iconName="remove" onClick={() => handleDeleteDevice(item)} ariaLabel="Delete device" />
                    </>
                  )}
                </SpaceBetween>
              )
            }
          ]}
          items={devices}
          loading={loading}
          selectedItems={canManageDevices ? selectedItems : undefined}
          onSelectionChange={canManageDevices ? ({ detail }) => setSelectedItems(detail.selectedItems) : undefined}
          selectionType={canManageDevices ? "multi" : undefined}
          trackBy="id"
          empty={
            <Box textAlign="center" color="inherit">
              <b>No devices</b>
              <Box variant="p" color="inherit">
                No devices have been registered yet.
              </Box>
            </Box>
          }
          header={
            <Header
              counter={`(${devices.length})`}
              description="Manage registered ESP32 devices and their access permissions"
            >
              Devices
            </Header>
          }
        />
      </Container>

      {/* Boot LED Status Reference */}
      <Container header={<Header variant="h3">ESP32 Boot LED Status Reference</Header>}>
        <SpaceBetween size="xs">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#22c55e', border: '1px solid #ccc' }} />
            <span>LED 1 — Setup Complete</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#22c55e', border: '1px solid #ccc' }} />
            <span>LED 2 — WiFi Connected</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#22c55e', border: '1px solid #ccc' }} />
            <span>LED 3 — Device Key Validated</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#22c55e', border: '1px solid #ccc' }} />
            <span>LED 4 — Location List Received</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#22c55e', border: '1px solid #ccc' }} />
            <span>LED 5 — Location Data Loaded</span>
          </div>
          <Box variant="small" color="text-body-secondary">
            Red = step failed. Blue pulsing = waiting for config. After boot, LEDs show weather data colors.
          </Box>
        </SpaceBetween>
      </Container>

      {/* Edit Device Modal */}
      <Modal
        visible={showEditModal}
        onDismiss={() => setShowEditModal(false)}
        header="Edit Device"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveDevice}>
                Save Changes
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {editingDevice && (
          <Form>
            <SpaceBetween direction="vertical" size="l">
              <FormField label="Device Name">
                <Input
                  value={editingDevice.deviceName}
                  onChange={({ detail }) =>
                    setEditingDevice({ ...editingDevice, deviceName: detail.value })
                  }
                />
              </FormField>
              <FormField label="Owner">
                <Input
                  value={editingDevice.userEmail ? `${editingDevice.username} (${editingDevice.userEmail})` : editingDevice.username || 'Unknown'}
                  disabled
                />
              </FormField>
              {canManageDevices && (
                <FormField label="Authorized Data Products">
                  <Multiselect
                    selectedOptions={editingDevice.authorizedDataProducts.map(product => ({
                      label: dataProductOptions.find(opt => opt.value === product)?.label || product,
                      value: product
                    }))}
                    onChange={({ detail }) =>
                      setEditingDevice({
                        ...editingDevice,
                        authorizedDataProducts: detail.selectedOptions.map(opt => opt.value!)
                      })
                    }
                    options={dataProductOptions}
                    placeholder="Select data products"
                  />
                </FormField>
              )}
              {!canManageDevices && editingDevice.authorizedDataProducts.length > 0 && (
                <FormField label="Authorized Data Products">
                  <SpaceBetween direction="horizontal" size="xs">
                    {editingDevice.authorizedDataProducts.map(product => (
                      <Badge key={product} color="blue">
                        {dataProductOptions.find(opt => opt.value === product)?.label || product}
                      </Badge>
                    ))}
                  </SpaceBetween>
                </FormField>
              )}
              {canManageDevices && (
                <FormField 
                  label="Assigned Location List" 
                  description="Select a location list from the device owner's lists"
                >
                <Select
                  selectedOption={
                    editingDevice.assignedLocationList
                      ? (() => {
                          // Look up the actual list to get the correct location count
                          const actualList = userLocationLists[editingDevice.userId]?.find(
                            list => list.data_product === editingDevice.assignedLocationList?.dataProduct
                          )
                          const locationCount = actualList?.location_count || 0
                          return {
                            label: `${dataProductOptions.find(opt => opt.value === editingDevice.assignedLocationList?.dataProduct)?.label || editingDevice.assignedLocationList.dataProduct} (${locationCount} locations)`,
                            value: editingDevice.assignedLocationList.dataProduct
                          }
                        })()
                      : null
                  }
                  onChange={({ detail }) => {
                    const selectedList = userLocationLists[editingDevice.userId]?.find(
                      list => list.data_product === detail.selectedOption?.value
                    )
                    setEditingDevice({
                      ...editingDevice,
                      assignedLocationList: selectedList
                        ? {
                            dataProduct: selectedList.data_product,
                            locationCount: selectedList.location_count || 0
                          }
                        : undefined
                    })
                  }}
                  options={
                    userLocationLists[editingDevice.userId]?.map(list => ({
                      label: `${dataProductOptions.find(opt => opt.value === list.data_product)?.label || list.data_product} (${list.location_count || 0} locations)`,
                      value: list.data_product
                    })) || []
                  }
                  placeholder={
                    loadingLocationLists
                      ? "Loading location lists..."
                      : userLocationLists[editingDevice.userId]?.length > 0
                      ? "Select a location list"
                      : "No location lists available"
                  }
                  disabled={loadingLocationLists || !userLocationLists[editingDevice.userId]?.length}
                  empty="No location lists found for this user"
                />
              </FormField>
              )}
              
              <Header variant="h3">LED Configuration</Header>
              
              <FormField label="LED Count" description="Number of LEDs in the strip (1-300)">
                <Input
                  type="number"
                  value={editingDevice.configuration?.ledCount.toString() || '30'}
                  onChange={({ detail }) =>
                    setEditingDevice({
                      ...editingDevice,
                      configuration: {
                        ...editingDevice.configuration!,
                        ledCount: parseInt(detail.value) || 30
                      }
                    })
                  }
                />
              </FormField>
              
              <FormField label="LED Pin" description="GPIO pin for LED data (0-39)">
                <Input
                  type="number"
                  value={editingDevice.configuration?.ledPin.toString() || '2'}
                  onChange={({ detail }) =>
                    setEditingDevice({
                      ...editingDevice,
                      configuration: {
                        ...editingDevice.configuration!,
                        ledPin: parseInt(detail.value) || 2
                      }
                    })
                  }
                />
              </FormField>
              
              <Header variant="h3">Brightness Settings</Header>
              
              <FormField label="Day Brightness" description="LED brightness during day (0-255)">
                <Input
                  type="number"
                  value={editingDevice.configuration?.brightness.toString() || '128'}
                  onChange={({ detail }) =>
                    setEditingDevice({
                      ...editingDevice,
                      configuration: {
                        ...editingDevice.configuration!,
                        brightness: parseInt(detail.value) || 128
                      }
                    })
                  }
                />
              </FormField>
              
              <FormField label="Night Brightness" description="LED brightness during night/sleep mode (0-255)">
                <Input
                  type="number"
                  value={editingDevice.configuration?.nightModeBrightness.toString() || '32'}
                  onChange={({ detail }) =>
                    setEditingDevice({
                      ...editingDevice,
                      configuration: {
                        ...editingDevice.configuration!,
                        nightModeBrightness: parseInt(detail.value) || 32
                      }
                    })
                  }
                />
              </FormField>
              
              <Header variant="h3">Data Polling</Header>
              
              <FormField label="Polling Interval" description="How often to fetch data in seconds (60-3600)">
                <Input
                  type="number"
                  value={editingDevice.configuration?.pollingInterval.toString() || '300'}
                  onChange={({ detail }) =>
                    setEditingDevice({
                      ...editingDevice,
                      configuration: {
                        ...editingDevice.configuration!,
                        pollingInterval: parseInt(detail.value) || 300
                      }
                    })
                  }
                />
              </FormField>
              
              <Header variant="h3">LED Legend</Header>
              
              <FormField label="Enable LED Legend" description="Display a separate LED strip showing color theme legend with band rotation">
                <Select
                  selectedOption={
                    editingDevice.configuration?.legendEnabled
                      ? { label: 'Enabled', value: 'true' }
                      : { label: 'Disabled', value: 'false' }
                  }
                  onChange={({ detail }) =>
                    setEditingDevice({
                      ...editingDevice,
                      configuration: {
                        ...editingDevice.configuration!,
                        legendEnabled: detail.selectedOption?.value === 'true'
                      }
                    })
                  }
                  options={[
                    { label: 'Enabled', value: 'true' },
                    { label: 'Disabled', value: 'false' }
                  ]}
                />
              </FormField>
              
              {editingDevice.configuration?.legendEnabled && (
                <FormField label="Rotation Interval (seconds)" description="How often to rotate through HF bands (1-60 seconds, default 5)">
                  <Input
                    type="number"
                    value={editingDevice.configuration?.rotationIntervalSeconds?.toString() || '5'}
                    onChange={({ detail }) =>
                      setEditingDevice({
                        ...editingDevice,
                        configuration: {
                          ...editingDevice.configuration!,
                          rotationIntervalSeconds: parseInt(detail.value) || 5
                        }
                      })
                    }
                  />
                </FormField>
              )}
              
              <Header variant="h3">Sleep Mode</Header>
              
              <FormField label="Enable Sleep Mode" description="Turn off LEDs during specified hours">
                <Select
                  selectedOption={
                    editingDevice.configuration?.sleepMode?.enabled
                      ? { label: 'Enabled', value: 'true' }
                      : { label: 'Disabled', value: 'false' }
                  }
                  onChange={({ detail }) =>
                    setEditingDevice({
                      ...editingDevice,
                      configuration: {
                        ...editingDevice.configuration!,
                        sleepMode: {
                          ...editingDevice.configuration!.sleepMode!,
                          enabled: detail.selectedOption?.value === 'true'
                        }
                      }
                    })
                  }
                  options={[
                    { label: 'Enabled', value: 'true' },
                    { label: 'Disabled', value: 'false' }
                  ]}
                />
              </FormField>
              
              {editingDevice.configuration?.sleepMode?.enabled && (
                <>
                  <FormField label="Sleep Start Time" description="Hour and minute when LEDs turn off">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Input
                        type="number"
                        value={editingDevice.configuration?.sleepMode?.startHour.toString() || '22'}
                        onChange={({ detail }) =>
                          setEditingDevice({
                            ...editingDevice,
                            configuration: {
                              ...editingDevice.configuration!,
                              sleepMode: {
                                ...editingDevice.configuration!.sleepMode!,
                                startHour: parseInt(detail.value) || 22
                              }
                            }
                          })
                        }
                        placeholder="Hour (0-23)"
                      />
                      <Input
                        type="number"
                        value={editingDevice.configuration?.sleepMode?.startMinute.toString() || '0'}
                        onChange={({ detail }) =>
                          setEditingDevice({
                            ...editingDevice,
                            configuration: {
                              ...editingDevice.configuration!,
                              sleepMode: {
                                ...editingDevice.configuration!.sleepMode!,
                                startMinute: parseInt(detail.value) || 0
                              }
                            }
                          })
                        }
                        placeholder="Minute (0-59)"
                      />
                    </SpaceBetween>
                  </FormField>
                  
                  <FormField label="Sleep End Time" description="Hour and minute when LEDs turn back on">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Input
                        type="number"
                        value={editingDevice.configuration?.sleepMode?.endHour.toString() || '7'}
                        onChange={({ detail }) =>
                          setEditingDevice({
                            ...editingDevice,
                            configuration: {
                              ...editingDevice.configuration!,
                              sleepMode: {
                                ...editingDevice.configuration!.sleepMode!,
                                endHour: parseInt(detail.value) || 7
                              }
                            }
                          })
                        }
                        placeholder="Hour (0-23)"
                      />
                      <Input
                        type="number"
                        value={editingDevice.configuration?.sleepMode?.endMinute.toString() || '0'}
                        onChange={({ detail }) =>
                          setEditingDevice({
                            ...editingDevice,
                            configuration: {
                              ...editingDevice.configuration!,
                              sleepMode: {
                                ...editingDevice.configuration!.sleepMode!,
                                endMinute: parseInt(detail.value) || 0
                              }
                            }
                          })
                        }
                        placeholder="Minute (0-59)"
                      />
                    </SpaceBetween>
                  </FormField>
                  
                  <FormField label="Timezone" description="Timezone for sleep mode schedule">
                    <Select
                      selectedOption={
                        timezoneOptions.find(
                          opt => opt.value === editingDevice.configuration?.sleepMode?.timezone
                        ) || timezoneOptions[0]
                      }
                      onChange={({ detail }) =>
                        setEditingDevice({
                          ...editingDevice,
                          configuration: {
                            ...editingDevice.configuration!,
                            sleepMode: {
                              ...editingDevice.configuration!.sleepMode!,
                              timezone: detail.selectedOption?.value || 'America/New_York'
                            }
                          }
                        })
                      }
                      options={timezoneOptions}
                    />
                  </FormField>
                </>
              )}
            </SpaceBetween>
          </Form>
        )}
      </Modal>

      {/* Register Device Modal */}
      <Modal
        visible={showRegisterModal}
        onDismiss={() => setShowRegisterModal(false)}
        header="Register New Device"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowRegisterModal(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleRegisterDevice}
                disabled={!newDevice.deviceName || !newDevice.userId}
              >
                Register Device
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween direction="vertical" size="l">
            <FormField label="Device Name" description="A descriptive name for this device">
              <Input
                value={newDevice.deviceName}
                onChange={({ detail }) =>
                  setNewDevice({ ...newDevice, deviceName: detail.value })
                }
                placeholder="e.g., Airport Weather Display"
              />
            </FormField>
            <FormField label="Owner" description="Select the user who owns this device">
              <Select
                selectedOption={userOptions.find(opt => opt.value === newDevice.userId) || null}
                onChange={({ detail }) =>
                  setNewDevice({ ...newDevice, userId: detail.selectedOption?.value || '' })
                }
                options={userOptions}
                placeholder={loadingUsers ? "Loading users..." : "Select a user"}
                disabled={loadingUsers}
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </Modal>

      {/* Auth Token Modal */}
      <Modal
        visible={showAuthTokenModal}
        onDismiss={() => {
          setShowAuthTokenModal(false)
          setAuthTokenData(null)
        }}
        header="Device Authentication Key"
        size="large"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button 
                variant="primary" 
                onClick={() => {
                  setShowAuthTokenModal(false)
                  setAuthTokenData(null)
                }}
              >
                Done
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {authTokenData && (
          <SpaceBetween direction="vertical" size="l">
            <Alert type="warning" header="Important: Save this key now!">
              This authentication key will only be shown once. Make sure to copy it and store it securely.
              You will need this key to configure your ESP32 device.
            </Alert>
            
            <Container>
              <SpaceBetween direction="vertical" size="m">
                <Box>
                  <Box variant="awsui-key-label">Device Name</Box>
                  <Box variant="p">{authTokenData.deviceName}</Box>
                </Box>
                
                <Box>
                  <Box variant="awsui-key-label">Device ID</Box>
                  <div style={{ fontFamily: 'monospace', padding: '8px 0' }}>{authTokenData.deviceId}</div>
                </Box>
                
                <Box>
                  <Box variant="awsui-key-label">Authentication Key</Box>
                  <div 
                    style={{ 
                      fontFamily: 'monospace', 
                      padding: '12px 16px',
                      backgroundColor: '#f2f3f3',
                      borderRadius: '4px',
                      wordBreak: 'break-all'
                    }}
                  >
                    {authTokenData.authToken}
                  </div>
                  <Box margin={{ top: 's' }}>
                    <Button 
                      variant="primary" 
                      iconName="copy"
                      onClick={handleCopyAuthToken}
                    >
                      Copy to Clipboard
                    </Button>
                  </Box>
                </Box>
              </SpaceBetween>
            </Container>

            <Alert type="info" header="Next Steps">
              <ol>
                <li>Copy the authentication key above</li>
                <li>Open your ESP32 firmware configuration</li>
                <li>Paste the key into the <code>DEVICE_KEY</code> constant</li>
                <li>Flash the firmware to your ESP32 device</li>
              </ol>
            </Alert>
          </SpaceBetween>
        )}
      </Modal>
    </SpaceBetween>
  )
}

export default Devices